// frontend/src/pages/VideoAnalytics.jsx

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  PlayCircleOutline as PlayIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Analytics as AnalyticsIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';

const VideoAnalytics = () => {
  const theme = useTheme();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchVideosWithStats();
  }, []);

  const fetchVideosWithStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/video-analytics/teacher/videos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch videos:', err);
      setError('動画の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}時間${minutes}分${secs}秒`;
    } else if (minutes > 0) {
      return `${minutes}分${secs}秒`;
    } else {
      return `${secs}秒`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleViewDetails = (videoId) => {
    navigate(`/video-analytics/${videoId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ 
        p: 4, 
        backgroundColor: theme.palette.mode === "dark" ? "#1A1A1A" : "#212121", 
        color: theme.palette.common.white,
        borderRadius: 2
      }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: theme.palette.common.white }}>
            <AnalyticsIcon sx={{ mr: 2, fontSize: 35, color: theme.palette.info.main }} />
            動画分析ダッシュボード
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.grey[300] }}>
            投稿した動画の視聴状況と学習者の行動を分析できます
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {videos.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <PlayIcon sx={{ fontSize: 80, color: theme.palette.grey[600], mb: 2 }} />
            <Typography variant="h6" sx={{ color: theme.palette.grey[400] }}>
              まだ動画を投稿していません
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => navigate('/teacher-dashboard')}
           >
              動画を投稿する
            </Button>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {videos.map((video) => (
              <Grid item xs={12} md={6} lg={4} key={video.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: theme.palette.mode === "dark" ? "#262626" : "#333333",
                    color: theme.palette.common.white,
                    border: `1px solid ${theme.palette.grey[700]}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      borderColor: theme.palette.grey[600]
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 240 }}>
                    <Typography variant="h6" gutterBottom noWrap sx={{ color: theme.palette.common.white }}>
                      {video.title}
                    </Typography>
                    
                    <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={formatDuration(video.duration)}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: theme.palette.grey[700], color: theme.palette.grey[300] }}
                      />
                      <Chip
                        label={formatDate(video.created_at)}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: theme.palette.grey[700], color: theme.palette.grey[300] }}
                      />
                    </Box>

                    <Divider sx={{ my: 2, borderColor: theme.palette.grey[700] }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PeopleIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                          <Box>
                            <Typography variant="h5" sx={{ color: theme.palette.common.white }}>
                              {video.unique_viewers}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.grey[400] }}>
                              視聴者数
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <EventIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
                          <Box>
                            <Typography variant="h5" sx={{ color: theme.palette.common.white }}>
                              {video.total_events}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.grey[400] }}>
                              再生回数
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* 常に表示して高さを揃える */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ color: theme.palette.grey[400] }}>
                        平均視聴回数: {video.unique_viewers > 0 ? (video.total_events / video.unique_viewers).toFixed(1) : '—'}回/人
                      </Typography>
                    </Box>

                    {/* 余白でカード高さを安定させる */}
                    <Box sx={{ flexGrow: 1 }} />
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="info"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewDetails(video.id)}
                      sx={{ textTransform: 'none' }}
                    >
                      詳細分析を見る
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default VideoAnalytics;